import {
  DOCUMENT_TYPE,
  LANGUAGE,
  PAYLOAD_LANGUAGE,
  ROUTE_NAMES,
} from "@root/constants";
import { getTodayQuestions } from "@root/store/selectors/entities";
import { selectConGptFilesPopover } from "@root/store/selectors/entities/dashboard";
import {
  updateSelectedModel,
  updateThreadList,
  updateThreads,
} from "@root/store/slices/entities";
import { getCurrentLanguage } from "@root/store/selectors/features";
import { getThreads } from "@root/store/thunks";
import {
  doQuery,
  doQueryDrawingAnalysis,
  doQueryKCS,
  doQueryMultiDoc,
  doQueryProject,
} from "@root/store/thunks/chatThunks";
import { generateRandomId } from "@root/utils/dateHelper";
import { WEB_PROJECTS } from "@root/webConstants";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useSystem from "./useSystem";
import { getSelectedModel } from "@root/store/selectors/entities/thread";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useProjectsContext } from "@root/context/projects-context";

const useProjectsDashboard = () => {
  const defaultState = {
    riskCheckerInput: "",
    conGptInput: "",
    doazGptInput: "",
  };
  const modelOptions: any = JSON.parse(
    process.env.REACT_APP_CHAT_MODELES ?? "[]"
  );
  const { t } = useTranslation("common");
  const [state, setState] = useState(defaultState);
  const [selectedField, setSelectedField] = useState({ id: 1 });
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const conGptPopover = useSelector(selectConGptFilesPopover);
  const doazGptInputRef = useRef<any>(null);
  const conGptInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOnline } = useSystem();
  // @ts-ignore
  const questions: any = useSelector(getTodayQuestions)?.rows || [];
  const riskCheckerQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.RISK_CHECKER.id
  );
  const conGptQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.CON_GPT.id
  );
  const doazGptQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.DOAZ_GPT.id
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedModel = useSelector(getSelectedModel);

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setState({ ...defaultState, [e.target.name]: e.target.value });
  };

  const currentLanguage = useSelector(getCurrentLanguage);

  const validator = (question?: string) => {
    if (question && question.length > 0) {
      return true;
    }
    if (!isOnline) {
      return false;
    }
    if (!state.doazGptInput.length || state.doazGptInput.length === 0) {
      return false;
    }
    return true;
  };

  const {
    getInitialThreads,
    setCurrentMessageResponse,
    currentPathname,
    docChatMode,
    selectedProject,
    getSource,
    getMode,
    getFilesPath,
    getDocNames,
    getPdfPageNum,
    setInput,
    setShowWelcomeText,
    getFileNameMain,
    setAttachedFile,
    model,
    getModel,
    getFileNamesArray,
    getFileId,
    getPdfId,
  } = useMainChatContext();

  const { handleProjectClick, projectsData, getFilename, documentData } =
    useProjectsContext();
    const isProjectDocumentChat = currentPathname?.startsWith(ROUTE_NAMES.DOCUMENT_CHAT) || currentPathname?.startsWith(ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT);

  const onSubmitHandler = (inputName: any) => {
    setCurrentMessageResponse?.(null);
    dispatch(updateThreadList([] as any));
    const input = inputName || state.doazGptInput;
    const queryPayload = {
      payload: {
        pdf_id: getPdfId(),
        query: input || "",
        s3_file_path: getFilesPath(), // Used in multi document chatbot
        doc_name: getDocNames(), // Used in multi document chatbot
        source: getSource(),
        mode: getMode()?.toString(),
        project_id: getMode(),
        fileName: getFileNameMain(),
        file_name: getFileNamesArray(), // Currently relevant in project document chatbot
        file_id: getFileId(), // Currently relevant in project document chatbot and multi-doc analysis
        page_no: getPdfPageNum(),
        model: getModel(),
        language:
          currentLanguage === LANGUAGE.ENGLISH
            ? PAYLOAD_LANGUAGE.ENGLISH
            : PAYLOAD_LANGUAGE.KOREAN,
        chat_history: [],
      },
      id: generateRandomId("chat"),
      onSuccess: () => {
        dispatch(
          getThreads({
            onSuccess: (res: any) => dispatch(updateThreads(res)),
          })
        );
        getInitialThreads();
      },
    };
    if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
      navigate(ROUTE_NAMES.KCS_CHAT);
      dispatch(doQueryKCS(queryPayload));
    } else if (currentPathname?.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS)) {
      if (currentPathname?.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
        dispatch(doQueryDrawingAnalysis(queryPayload));
      } else {
        setInput(input);
        navigate(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT);
      }
    } else if (currentPathname?.includes("document")) {
      navigate(ROUTE_NAMES.DOCUMENT_CHAT);
      dispatch(doQueryProject(queryPayload));
      if (docChatMode?.value === "project") {
        const project = projectsData?.rows?.find(
          (project: any) => project?.name === selectedProject
        );
        if (project) {
          handleProjectClick(project);
        }
      }
    } else if (currentPathname?.includes(DOCUMENT_TYPE.CHECKLIST)) {
      navigate(ROUTE_NAMES.CHECKLIST_CHAT);
      dispatch(doQueryMultiDoc(queryPayload));
    } else {
      navigate(ROUTE_NAMES.KSCE_CHAT_SCREEN);
      dispatch(doQuery(queryPayload));
    }
    if (currentPathname !== ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD) {
      setInput("");
    }
    setShowWelcomeText(false);
    if (!currentPathname?.includes(ROUTE_NAMES.CHECKLIST) && !isProjectDocumentChat) {
      setAttachedFile([]);
    }
  };

  const onCardClick = (q: string) => {
    onSubmitHandler(q);
  };

  const onModelChange = (option: any) => {
    dispatch(updateSelectedModel(option));
  };

  return {
    t,
    state,
    inputChangeHandler,
    selectedField,
    setSelectedField,
    selectedFiles,
    inputRef,
    conGptPopover,
    doazGptInputRef,
    conGptInputRef,
    onSubmitHandler,
    riskCheckerQuestions,
    conGptQuestions,
    doazGptQuestions,
    questions,
    onCardClick,
    onModelChange,
    modelOptions,
  };
};

export default useProjectsDashboard;
